import type { CaptureResult, PostHog } from "posthog-js";

import { ENVS, isPosthogEnabled, isPosthogSdkEnabled } from "config";
import type {
  PostHogClientWithSupportIdentity,
  PostHogWithConversations,
} from "types/posthog-conversations";
import { isPosthogSupportIdentityConfigured } from "types/posthog-conversations";
import { hasAnalyticsConsent } from "lib/analytics/consent";
import { POSTHOG_PAGELEAVE_EVENT, RIVETAI_ANALYTICS_SURFACE } from "lib/analytics/posthog-pageview";
import {
  fetchPosthogConversationsRemoteConfig,
  resolvePosthogClientHosts,
} from "config/posthog-client";
import { POSTHOG_RELEASE_NAME } from "config/posthog-release";
import { POSTHOG_SESSION_RECORDING_OPTIONS } from "config/posthog-session-recording";
import { getAcquisitionAttributionForEvent } from "utils/readAcquisitionAttribution";
import { ANALYTICS_SCHEMA_VERSION } from "../product-events/events";

let initPromise: Promise<PostHog | null> | null = null;
let posthogInstance: PostHog | null = null;
let initializedPosthogKey: string | null = null;

const resetPosthogSdkState = (): void => {
  if (posthogInstance) {
    try {
      posthogInstance.reset();
    } catch (error) {
      console.error("PostHog reset failed during SDK re-init:", error);
    }
  }
  posthogInstance = null;
  initPromise = null;
  initializedPosthogKey = null;
};

const ensurePosthogKeyMatchesInitializedInstance = (): void => {
  const currentKey = ENVS.posthogKey?.trim() ?? "";
  if (initializedPosthogKey && initializedPosthogKey !== currentKey) {
    resetPosthogSdkState();
  }
};

export const dropPlainAutocaptureEvents = (event: CaptureResult | null): CaptureResult | null =>
  event?.event === "$autocapture" ? null : event;

const scheduleIdle = (callback: () => void): void => {
  if (typeof window === "undefined") return;
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout: 4000 });
    return;
  }
  setTimeout(callback, 200);
};

export const getPosthogIfReady = (): PostHog | null => posthogInstance;

export const initPosthogDeferred = (): Promise<PostHog | null> => {
  if (!isPosthogSdkEnabled || typeof window === "undefined" || !hasAnalyticsConsent()) {
    return Promise.resolve(null);
  }

  ensurePosthogKeyMatchesInitializedInstance();

  if (initPromise) {
    return initPromise;
  }

  const currentKey = ENVS.posthogKey.trim();

  initPromise = new Promise((resolve) => {
    scheduleIdle(() => {
      void (async () => {
        try {
          const posthog = (await import("posthog-js")).default;
          // Bundle the conversations extension instead of relying on PostHog's
          // runtime external script fetch (often blocked by ad blockers / devtools).
          await import("posthog-js/dist/conversations.js");
          const { apiHost, uiHost } = resolvePosthogClientHosts();
          posthog.init(currentKey, {
            api_host: apiHost,
            ui_host: uiHost,
            capture_pageview: false,
            capture_pageleave: isPosthogEnabled,
            // Rage clicks are detected inside the autocapture click handler, so
            // autocapture must stay on for clicks. Plain $autocapture events are
            // dropped in before_send so only $rageclick and $dead_click get through.
            autocapture: { dom_event_allowlist: ["click"] },
            rageclick: true,
            before_send: dropPlainAutocaptureEvents,
            ...(isPosthogEnabled
              ? {
                  capture_exceptions: {
                    capture_unhandled_errors: true,
                    capture_unhandled_rejections: true,
                    capture_console_errors: false,
                  },
                  session_recording: POSTHOG_SESSION_RECORDING_OPTIONS,
                }
              : {
                  disable_session_recording: true,
                }),
          });

          posthog.register({ rivetai_surface: RIVETAI_ANALYTICS_SURFACE.productApp });

          const releaseVersion = ENVS.releaseVersion;
          if (releaseVersion) {
            posthog.register({
              rivetai_release_name: POSTHOG_RELEASE_NAME,
              rivetai_release_version: releaseVersion,
            });
          }

          const attribution = getAcquisitionAttributionForEvent();
          if (Object.keys(attribution).length > 0) {
            posthog.register(attribution);
          }

          posthogInstance = posthog;
          initializedPosthogKey = currentKey;
          resolve(posthog);
        } catch {
          resolve(null);
        }
      })();
    });
  });

  return initPromise;
};

const withPosthog = async (run: (posthog: PostHog) => void): Promise<void> => {
  const posthog = await initPosthogDeferred();
  if (!posthog) return;
  try {
    run(posthog);
  } catch (error) {
    console.error("PostHog operation failed:", error);
  }
};

export const capturePosthogEvent = (event: string, properties?: Record<string, unknown>): void => {
  if (!isPosthogEnabled) return;
  void withPosthog((posthog) => {
    const anonymousId = posthog.get_property("$device_id");
    posthog.capture(event, {
      analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
      rivetai_surface: RIVETAI_ANALYTICS_SURFACE.productApp,
      ...(typeof anonymousId === "string" && anonymousId ? { anonymous_id: anonymousId } : {}),
      ...properties,
    });
  });
};

export const capturePosthogPageLeave = (properties?: Record<string, unknown>): void => {
  if (!isPosthogEnabled) return;
  void withPosthog((posthog) => {
    posthog.capture(POSTHOG_PAGELEAVE_EVENT, properties);
  });
};

export const capturePosthogException = (
  error: Error,
  properties?: Record<string, unknown>
): void => {
  if (!isPosthogEnabled) return;
  void withPosthog((posthog) => {
    posthog.captureException(error, properties);
  });
};

export const identifyPosthogUser = (userId: string, properties?: Record<string, unknown>): void => {
  if (!isPosthogEnabled) return;
  void withPosthog((posthog) => {
    posthog.identify(userId, properties);
    if (properties?.is_internal_user === true) {
      posthog.opt_out_capturing();
    }
  });
};

const asPosthogSupportClient = (posthog: PostHog): PostHogClientWithSupportIdentity =>
  posthog as unknown as PostHogClientWithSupportIdentity;

const clearSupportIdentityOn = (posthog: PostHog): void => {
  const sdk = posthog as PostHog & PostHogWithConversations;
  asPosthogSupportClient(posthog).clearIdentity();
  sdk.conversations?.hide();
};

export const setPosthogSupportIdentity = async (
  userId: string,
  identityHash: string
): Promise<boolean> => {
  if (!isPosthogSdkEnabled || !userId.trim() || !identityHash.trim()) {
    return false;
  }

  const posthog = await initPosthogDeferred();
  if (!posthog) {
    return false;
  }

  try {
    const client = asPosthogSupportClient(posthog);
    client.setIdentity(userId.trim(), identityHash.trim());
    return isPosthogSupportIdentityConfigured(client);
  } catch (error) {
    console.error("PostHog setIdentity failed:", error);
    return false;
  }
};

export const getPosthogSupportIdentityConfigured = async (): Promise<boolean> => {
  const posthog = await initPosthogDeferred();
  if (!posthog) {
    return false;
  }

  return isPosthogSupportIdentityConfigured(asPosthogSupportClient(posthog));
};

export const clearPosthogSupportIdentity = (): void => {
  if (!isPosthogSdkEnabled) return;
  if (posthogInstance) {
    clearSupportIdentityOn(posthogInstance);
    return;
  }
  void withPosthog(clearSupportIdentityOn);
};

/**
 * posthog.reset() also drops the Support (conversations) remote config, and the SDK only reads
 * that config once per page load (a second posthog.init() is a no-op). After an in-app logout
 * the next account's support chat stayed "still loading" until a full refresh (RV-1067).
 * Set on reset; the next support availability check re-applies the project's config.
 */
const SUPPORT_CONFIG_TIMEOUT_MS = 5_000;
let conversationsConfigClearedByReset = false;
let conversationsConfigRestore: Promise<void> | null = null;
let supportResetGeneration = 0;

const applySupportConfig = async (generation: number): Promise<void> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), SUPPORT_CONFIG_TIMEOUT_MS);
  try {
    const posthog = (await initPosthogDeferred()) as (PostHog & PostHogWithConversations) | null;
    // A logout while the SDK loaded or the request ran starts over; this belongs to the
    // earlier session.
    if (!posthog?.conversations || generation !== supportResetGeneration) return;
    const config = await fetchPosthogConversationsRemoteConfig(controller.signal);
    if (generation !== supportResetGeneration) return;
    posthog.conversations.onRemoteConfig({ conversations: config });
    conversationsConfigClearedByReset = false;
  } catch (error) {
    console.warn("[RivetAI Support] Could not reload PostHog Support config:", error);
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Re-applies the Support config after a reset. Runs from the signed-in app's support check,
 * not at logout, so the login page gets no launcher. A failed or timed-out request leaves the
 * config marked as cleared, so the next check tries again instead of treating Support as off.
 */
export const restorePosthogConversationsConfig = (): Promise<void> => {
  if (!conversationsConfigClearedByReset) return Promise.resolve();
  if (conversationsConfigRestore) return conversationsConfigRestore;

  const restore = applySupportConfig(supportResetGeneration).finally(() => {
    if (conversationsConfigRestore === restore) conversationsConfigRestore = null;
  });
  conversationsConfigRestore = restore;
  return restore;
};

export const resetPosthog = (): void => {
  if (!isPosthogSdkEnabled) return;
  // Every logout outdates a restore in flight, even one still waiting for the SDK to load.
  supportResetGeneration += 1;
  conversationsConfigRestore = null;
  if (posthogInstance) {
    try {
      clearSupportIdentityOn(posthogInstance);
      posthogInstance.reset();
      conversationsConfigClearedByReset = true;
    } catch (error) {
      console.error("PostHog reset failed:", error);
    }
  }
  posthogInstance = null;
  initPromise = null;
  initializedPosthogKey = null;
};

export const setPosthogGroup = (
  groupType: string,
  groupId: string,
  properties?: Record<string, unknown>
): void => {
  if (!isPosthogEnabled) return;
  void withPosthog((posthog) => {
    posthog.group(groupType, groupId, properties);
  });
};

export const registerPosthogProperties = (properties: Record<string, unknown>): void => {
  if (!isPosthogEnabled) return;
  void withPosthog((posthog) => {
    posthog.register(properties);
  });
};
